import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { channelsApi } from '@/api/channels';
import { useChatStore } from '@/store/chatStore';
import Modal from '@/components/ui/Modal';
import { extractError } from '@/api/client';
import { getSocket } from '@/lib/socket';

const schema = z.object({
  name: z
    .string()
    .min(2, 'Минимум 2 символа')
    .max(64)
    .regex(/^[a-z0-9_-]+$/, 'Только строчные буквы, цифры, _ и -'),
  displayName: z.string().min(1, 'Обязательное поле').max(64),
  topic: z.string().max(256).optional(),
  type: z.enum(['PUBLIC', 'PRIVATE']).default('PUBLIC'),
  mediaEnabled: z.boolean().default(true),
  reactionsEnabled: z.boolean().default(true),
  membersCanPost: z.boolean().default(true),
  classicStyle: z.boolean().default(false),
});

type FormData = z.infer<typeof schema>;

interface Props { open: boolean; onClose: () => void; }

export default function CreateChannelModal({ open, onClose }: Props) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { addJoinedChannel, setActiveChannel } = useChatStore();

  const { register, handleSubmit, reset, formState: { errors } } = useForm<FormData>({
    resolver: zodResolver(schema),
    defaultValues: { type: 'PUBLIC', mediaEnabled: true, reactionsEnabled: true, membersCanPost: true, classicStyle: false },
  });

  const mutation = useMutation({
    mutationFn: channelsApi.create,
    onSuccess: (channel) => {
      queryClient.invalidateQueries({ queryKey: ['channels'] });
      addJoinedChannel(channel);
      setActiveChannel(channel.id);
      // Subscribe socket to the new channel room so realtime events are received
      try { getSocket().emit('channel:join', { channelId: channel.id }); } catch {}
      navigate(`/channel/${channel.id}`);
      reset();
      onClose();
    },
  });

  const onSubmit = (data: FormData) => {
    const { classicStyle, ...rest } = data;
    const payload = { ...rest, chatStyle: classicStyle ? 'CLASSIC' : 'BUBBLE' };
    mutation.mutate(payload as Parameters<typeof channelsApi.create>[0]);
  };

  return (
    <Modal open={open} onClose={onClose} title="Создать канал">
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        {mutation.error && (
          <div className="bg-red-900/40 border border-red-700 text-red-300 text-sm rounded-lg px-3 py-2">
            {extractError(mutation.error)}
          </div>
        )}

        <div>
          <label className="block text-surface-300 text-xs font-medium mb-1.5">Системное имя</label>
          <input {...register('name')} className="irc-input" placeholder="moy-kanal" />
          {errors.name && <p className="text-red-400 text-xs mt-1">{errors.name.message}</p>}
        </div>

        <div>
          <label className="block text-surface-300 text-xs font-medium mb-1.5">Отображаемое название</label>
          <input {...register('displayName')} className="irc-input" placeholder="#Мой канал" />
          {errors.displayName && (
            <p className="text-red-400 text-xs mt-1">{errors.displayName.message}</p>
          )}
        </div>

        <div>
          <label className="block text-surface-300 text-xs font-medium mb-1.5">Тема (необязательно)</label>
          <input {...register('topic')} className="irc-input" placeholder="О чём этот канал?" />
        </div>

        <div>
          <label className="block text-surface-300 text-xs font-medium mb-1.5">Тип</label>
          <select {...register('type')} className="irc-input">
            <option value="PUBLIC">Публичный</option>
            <option value="PRIVATE">Приватный (только по приглашению)</option>
          </select>
        </div>

        <label className="flex items-center gap-2 cursor-pointer">
          <input type="checkbox" {...register('mediaEnabled')} className="w-4 h-4 rounded border-surface-600 bg-surface-800 text-brand-500 focus:ring-brand-500" />
          <span className="text-surface-300 text-sm">Разрешить загрузку фото</span>
        </label>

        <label className="flex items-center gap-2 cursor-pointer">
          <input type="checkbox" {...register('reactionsEnabled')} className="w-4 h-4 rounded border-surface-600 bg-surface-800 text-brand-500 focus:ring-brand-500" />
          <span className="text-surface-300 text-sm">Разрешить реакции на сообщения</span>
        </label>

        <label className="flex items-center gap-2 cursor-pointer">
          <input type="checkbox" {...register('membersCanPost')} className="w-4 h-4 rounded border-surface-600 bg-surface-800 text-brand-500 focus:ring-brand-500" />
          <span className="text-surface-300 text-sm">Пользователи могут писать в канал</span>
        </label>
        <p className="text-xs text-surface-500 -mt-1">
          Если выключено — писать могут только админы канала. Остальные только читают и ставят реакции.
        </p>

        <label className="flex items-center gap-2 cursor-pointer">
          <input type="checkbox" {...register('classicStyle')} className="w-4 h-4 rounded border-surface-600 bg-surface-800 text-brand-500 focus:ring-brand-500" />
          <span className="text-surface-300 text-sm">Классический IRC-чат (без пузырьков, моноширинный)</span>
        </label>

        <div className="flex gap-3 justify-end pt-2">
          <button type="button" onClick={onClose} className="btn-ghost">Отмена</button>
          <button type="submit" disabled={mutation.isPending} className="btn-primary">
            {mutation.isPending ? 'Создаём…' : 'Создать канал'}
          </button>
        </div>
      </form>
    </Modal>
  );
}
